import React, { useState, useEffect } from "react";
import { X, Phone, KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { requestOtp, verifyOtp } from "@/lib/auth";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function SignInModal({ open, onClose }) {
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const { refresh, setUser } = useAuth();

  useEffect(() => {
    if (open) {
      setStep("phone");
      setPhone("");
      setOtp("");
      setDevOtp("");
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const submitPhone = async () => {
    const trimmed = (phone || "").trim();
    if (!/^\+?\d[\d\s-]{5,}$/.test(trimmed)) {
      toast.error("Drop a real phone number, pookie (include country code).");
      return;
    }
    setLoading(true);
    try {
      const data = await requestOtp(trimmed);
      setDevOtp(data.dev_otp || "");
      setStep("otp");
      toast.success("Code sent. (Dev mode: use 123456)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code.");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async () => {
    if (!/^\d{4,8}$/.test(otp)) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const data = await verifyOtp(phone.trim(), otp.trim());
      setUser(data.user);
      await refresh();
      toast.success(`Welcome, pookie. You're in.`);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wrong code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm"
      data-testid="signin-modal"
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
    >
      <div className="pl-card bg-white max-w-md w-full p-6 md:p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full border-[3px] border-black bg-white hover:bg-[#FFE600]"
          data-testid="signin-close"
          aria-label="Close"
        >
          <X size={18} strokeWidth={3} />
        </button>

        <div className="pl-chip bg-[#FFE600] mb-4">
          {step === "phone" ? (
            <>
              <Phone size={14} strokeWidth={3} /> Sign in
            </>
          ) : (
            <>
              <KeyRound size={14} strokeWidth={3} /> Verify code
            </>
          )}
        </div>

        {step === "phone" ? (
          <>
            <h2 className="font-display text-3xl tracking-tighter">Sign in with your phone</h2>
            <p className="font-mono-pl text-sm mt-2 text-black/70">
              Get a "My Cases" history and keep Pookie Pro tied to you across devices.
            </p>
            <label className="pl-label mt-6 block">Phone number</label>
            <input
              data-testid="signin-phone"
              autoFocus
              type="tel"
              className="pl-input mt-2"
              placeholder="+91 98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitPhone()}
            />
            <button
              onClick={submitPhone}
              disabled={loading}
              className="pl-btn pl-btn-primary w-full justify-center mt-5"
              data-testid="signin-request-otp"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Phone size={16} strokeWidth={3} />}
              Send code
            </button>
            <p className="font-mono-pl text-[11px] text-black/55 mt-3 text-center">
              Dev mode: any number works, code is always <b>123456</b>. We don't store anything but your phone.
            </p>
          </>
        ) : (
          <>
            <h2 className="font-display text-3xl tracking-tighter">Enter your code</h2>
            <p className="font-mono-pl text-sm mt-2 text-black/70">
              Code sent to <b>{phone}</b>. {devOtp && <>Dev OTP: <b>{devOtp}</b></>}
            </p>
            <label className="pl-label mt-6 block">6-digit code</label>
            <input
              data-testid="signin-otp"
              autoFocus
              inputMode="numeric"
              maxLength={6}
              className="pl-input mt-2 text-2xl tracking-[0.4em] text-center font-display"
              placeholder="123456"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && submitOtp()}
            />
            <button
              onClick={submitOtp}
              disabled={loading}
              className="pl-btn pl-btn-primary w-full justify-center mt-5"
              data-testid="signin-verify"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} strokeWidth={3} />}
              Verify & sign in
            </button>
            <button
              onClick={() => setStep("phone")}
              className="font-mono-pl text-xs text-black/60 underline mt-3 mx-auto block"
              data-testid="signin-change-phone"
            >
              Change phone number
            </button>
          </>
        )}
      </div>
    </div>
  );
}
